
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import httpClient from "services/network/httpClient";
import Input from "components/Input";
import * as yup from "yup";
import CustomSelect from "components/Select";
import PillButton from "components/button/Pills";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { showToast } from "components/Toaster";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faCity,
  faVenusMars,
  faMapMarkerAlt,
  faMapPin,
  faIdBadge,
  faBriefcase,
} from "@fortawesome/free-solid-svg-icons";

// ------------------ VALIDATION SCHEMA ------------------
const schema = yup.object().shape({
  username: yup.string().trim().required("Employee name is required"),
  email: yup
    .string()
    .trim()
    .required("Enter valid email (e.g., example@domain.com)")
    .email("Enter valid email address")
    .matches(
      /^[^\s@]+@[^\s@]+\.(com|in|org|net|edu|gov|info|co|ai)$/i,
      "Enter email with valid domain"
    ),
  city: yup.string().trim().required("City is required"),
  state: yup.string().trim().required("State is required"),
  address: yup.string().trim().required("Address is required"),
  countryCode: yup.string().required(),
  mobile: yup
    .string()
    .required("Mobile number is required")
    .test("is-ten-digits", "Mobile number must be exactly 10 digits", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      const cleaned = digits.startsWith("91") ? digits.slice(2) : digits;
      return cleaned.length === 10;
    }),

  postalCode: yup
    .string()
    .required("Postal code is required")
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),

  gender: yup.string().trim().required("Gender is required"),
  designation: yup.string().trim().required("Designation is required"),
  employeeCode: yup.string().trim().required("Employee code is required"),
  reportingToId: yup.number(),
});

interface AddProps {
  onClose: () => void;
  getData: () => void;
  emailData: any[];
}

const AddUser = ({ onClose, getData, emailData }: AddProps) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { countryCode: "+91" },
    mode: "all",
  });

  const [mobile, setMobile] = React.useState("");

  const submit = (data: any) => {
    let formattedMobile = mobile;
    if (!formattedMobile.startsWith("+")) {
      formattedMobile = `+${mobile}`;
    }

    const payload = {
      ...data,
      mobile: formattedMobile,
    };
    console.log("Payload : ", payload);

    delete payload.countryCode;

    httpClient
      .post("employees/create", payload)
      .then((response: any) => {
        console.log("API Response:", response);
        if (response.success) {
          showToast("Employee Added Successfully", "success");
        } else {
          showToast(response.error?.message || "Please check your inputs", "error");
        }
      })
      .catch(() => showToast("Something went wrong", "error"))
      .finally(() => {
        onClose();
        getData();
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)}>
        <div className="grid grid-cols-3 gap-3 p-3">

          {/* Username */}
          <Input
            name="username"
            label="Employee Name"
            placeholder="Enter Employee Name"
            icon={<FontAwesomeIcon icon={faUser} className="text-gray-400" />}
            required
            onlyLetters
          />
          <Input
            name="employeeCode"
            label="Employee Code"
            placeholder="Enter Employee Code"
            icon={<FontAwesomeIcon icon={faUser} className="text-gray-400" />}
            required

          />
          {/* Email */}
          <Input
            name="email"
            label="Email"
            placeholder="Enter Employee Email"
            icon={<FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />}
            noCapitalize
            autoComplete="email"
            required
          />

          {/* Mobile */}
          <div className="mb-3">
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative rounded-lg border border-gray-300">
              <PhoneInput
                country="in"
                value={mobile}
                onChange={(value) => {
                  setMobile(value);
                  methods.setValue("mobile", value, { shouldValidate: true });
                }}
                //phone input field background and dropdown background color
                inputClass="!w-full !bg-white !text-[#374151] dark:!bg-gray-800 dark:!text-white !border-none"
                buttonClass="!bg-white dark:!bg-gray-700"
                dropdownClass="
  !bg-white !text-[#374151] 
  dark:!bg-gray-800 dark:!text-white

  [&>.country:hover]:!bg-gray-100 
  [&>.country:hover]:!text-black
  dark:[&>.country:hover]:!bg-gray-700 
  dark:[&>.country:hover]:!text-white

  [&>.country.highlight]:!bg-gray-200 
  [&>.country.highlight]:!text-black
  dark:[&>.country.highlight]:!bg-gray-700 
  dark:[&>.country.highlight]:!text-white"//text color when cursor over the dropdown
                inputProps={{ name: "mobile", required: true }}
                searchClass="!text-black dark:!text-black !bg-white dark:!bg-gray-700" //seach field text color
                enableSearch
                inputStyle={{
                  width: "100%",
                  height: "38px",
                  borderRadius: "0.5rem",
                  border: "none",
                  fontSize: "14px",

                }}
                buttonStyle={{
                  border: "none",
                  background: "none",
                  borderRadius: "0.5rem 0 0 0.5rem",
                }}
                containerStyle={{
                  margin: "0",
                }}
              />
            </div>
            {methods.formState.errors.mobile && (
              <span className="text-red-500 text-sm font-medium mt-1">
                {methods.formState.errors.mobile.message as string}
              </span>
            )}
          </div>

          {/* Designation */}
          <Input
            name="designation"
            label="Designation"
            placeholder="Enter Designation"
            icon={<FontAwesomeIcon icon={faBriefcase} className="text-gray-400" />}
            required
            onlyLetters
          />

          {/* Gender */}
          <Input
            name="gender"
            label="Gender"
            placeholder="Enter Employee Gender"
            icon={<FontAwesomeIcon icon={faVenusMars} className="text-gray-400" />}
            required
            onlyLetters
          />

          {/* City */}
          <Input
            name="city"
            label="City"
            placeholder="Enter City"
            icon={<FontAwesomeIcon icon={faCity} className="text-gray-400" />}
            required
            onlyLetters
          />

          {/* State */}
          <Input
            name="state"
            label="State"
            placeholder="Enter State"
            icon={<FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />}
            required
            onlyLetters
          />

          {/* Reporting To */}
          <CustomSelect
            name="reportingToId"
            label="Select Reporting To"
            options={emailData}
            placeholder="Select Employee"
            size="lg"
            errorMessage={methods.formState.errors.reportingToId?.message as string}
          />

          {/* Address */}
          <div className="col-span-2">
            <Input
              name="address"
              label="Address"
              placeholder="Enter Employee address"
              type="textarea"
              icon={<FontAwesomeIcon icon={faMapPin} className="text-gray-400 mt-2" />}
              required
              maxLength={150}
              helperText="Max 150 chars"
            />
          </div>

          {/* Postal Code */}
          <Input
            name="postalCode"
            label="Postal Code"
            placeholder="Enter 6-digit Postal code"
            icon={<FontAwesomeIcon icon={faIdBadge} className="text-gray-400" />}
            required
            maxLength={6}
            onlyDigits
            helperText="Must be exactly 6 digits"
            onChange={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
              methods.setValue("postalCode", e.target.value, { shouldValidate: true });
            }}
          />
        </div>

        <div className="flex justify-end gap-4 mt-4 pr-3 pb-3">
          <PillButton type="submit" variant="secondary">Submit</PillButton>
          <PillButton type="reset" onClick={onClose} variant="primary">
            Cancel
          </PillButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddUser;




